import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import MarketLayout from '../components/MarketLayout';
import InvestorSidePanel from '../components/market/InvestorSidePanel';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const STATUS_STYLES = {
  approved:  { bg: '#f0fde4', color: '#111111' },
  pending:   { bg: '#fffde4', color: '#111111' },
  rejected:  { bg: '#ffe6e9', color: '#ff082f' },
  suspended: { bg: '#f3f4f6', color: '#374151' },
};

const TYPE_LABELS = {
  fund: 'Credit Fund',
  family_office: 'Family Office',
  reit: 'REIT',
  trade_desk: 'Trade Desk',
  individual: 'Individual',
  other: 'Other',
};

const TABS = [
  { key: 'all',       label: 'All' },
  { key: 'pending',   label: 'Pending' },
  { key: 'approved',  label: 'Approved' },
  { key: 'rejected',  label: 'Rejected' },
];

function StatusPill({ status }) {
  const s = STATUS_STYLES[status] || { bg: '#f3f4f6', color: '#374151' };
  return (
    <span style={{
      display: 'inline-block',
      padding: '3px 10px',
      borderRadius: 9999,
      backgroundColor: s.bg,
      color: s.color,
      fontSize: 11,
      fontWeight: 700,
      textTransform: 'capitalize',
    }}>
      {status || '—'}
    </span>
  );
}

function fmtDate(d) {
  if (!d) return '—';
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return '—';
  return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function MarketInvestors() {
  const [tab, setTab] = useState('all');
  const [investors, setInvestors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [rejecting, setRejecting] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  // Side panel state — investor object (or null when closed). Doc-count
  // map keyed by investor id powers the Docs column + NDA-status pill in
  // the row, refreshed when the side panel reports a change (upload /
  // delete / send NDA).
  const [panelInvestor, setPanelInvestor] = useState(null);
  const [docMeta, setDocMeta] = useState({}); // { [id]: { count, ndaStatus } }

  const token = localStorage.getItem('doorly_admin_token');

  const load = useCallback(async (statusFilter) => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API_URL}/api/admin/market/investors`, {
        params: { status: statusFilter },
        headers: { Authorization: `Bearer ${token}` },
      });
      setInvestors(res.data.investors || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load investors.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Hydrate doc counts + NDA status per row in the background after the
  // investor list lands. N requests, but N is bounded by a single page
  // of investors (rarely > 50). Keeps the table render snappy.
  const hydrateDocMeta = useCallback(async (rows) => {
    const ids = rows.map((r) => r.id);
    const next = {};
    await Promise.all(ids.map(async (id) => {
      try {
        const r = await axios.get(
          `${API_URL}/api/admin/market/investors/${id}/documents`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const docs = r.data.documents || [];
        const ndaDoc = docs.find((d) => d.document_type === 'nda');
        const ndaStatus = ndaDoc
          ? (['completed', 'signed'].includes(String(ndaDoc.boldsign_status).toLowerCase()) ? 'signed'
              : ['sent', 'pending_manual', 'reminder', 'viewed'].includes(String(ndaDoc.boldsign_status).toLowerCase()) ? 'pending'
              : ndaDoc.boldsign_status || 'uploaded')
          : null;
        next[id] = { count: docs.length, ndaStatus };
      } catch { next[id] = { count: 0, ndaStatus: null }; }
    }));
    setDocMeta(next);
  }, [token]);

  useEffect(() => { load(tab); }, [tab, load]);
  useEffect(() => { if (investors.length) hydrateDocMeta(investors); }, [investors, hydrateDocMeta]);

  const updateStatus = async (id, status, rejectionReason) => {
    setBusyId(id);
    try {
      await axios.patch(
        `${API_URL}/api/admin/market/investors/${id}`,
        { status, rejectionReason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await load(tab);
      setRejecting(null);
      setRejectReason('');
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to update status.');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <MarketLayout>
      <div style={{ padding: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 18 }}>
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 800, color: '#111827', margin: 0 }}>Investors</h1>
            <p style={{ fontSize: 13, color: '#6b7280', margin: '4px 0 0' }}>
              Approve, reject, or review DoorlyMarket investor accounts.
            </p>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 6, marginBottom: 14, borderBottom: '1px solid #e5e7eb' }}>
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              style={{
                padding: '10px 16px',
                fontSize: 13,
                fontWeight: 600,
                color: tab === t.key ? '#3a6ad9' : '#6b7280',
                backgroundColor: 'transparent',
                border: 'none',
                borderBottom: tab === t.key ? '2px solid #3a6ad9' : '2px solid transparent',
                cursor: 'pointer',
              }}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div style={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden' }}>
          {loading ? (
            <div style={{ padding: 32, textAlign: 'center', color: '#9ca3af', fontSize: 14 }}>Loading…</div>
          ) : error ? (
            <div style={{ padding: 32, textAlign: 'center', color: '#ff082f', fontSize: 14 }}>{error}</div>
          ) : investors.length === 0 ? (
            <div style={{ padding: 32, textAlign: 'center', color: '#9ca3af', fontSize: 14 }}>No investors found.</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ backgroundColor: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                  <Th>Name</Th>
                  <Th>Firm</Th>
                  <Th>Type</Th>
                  <Th>Status</Th>
                  <Th>Docs</Th>
                  <Th>Registered</Th>
                  <Th>Last Login</Th>
                  <Th align="right">Bids</Th>
                  <Th align="right">Actions</Th>
                  <Th align="right" />
                </tr>
              </thead>
              <tbody>
                {investors.map((i) => {
                  const meta = docMeta[i.id] || { count: 0, ndaStatus: null };
                  const isSelected = panelInvestor && panelInvestor.id === i.id;
                  return (
                  <tr
                    key={i.id}
                    onClick={() => setPanelInvestor(i)}
                    style={{
                      borderBottom: '1px solid #f3f4f6',
                      cursor: 'pointer',
                      backgroundColor: isSelected ? '#eff6ff' : 'transparent',
                      transition: 'background-color 0.1s',
                    }}
                    onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.backgroundColor = '#eff6ff'; }}
                    onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.backgroundColor = 'transparent'; }}
                  >
                    <Td>
                      <div style={{ fontWeight: 600, color: '#111827' }}>{i.first_name} {i.last_name}</div>
                      <div style={{ color: '#6b7280', fontSize: 12 }}>{i.email}</div>
                    </Td>
                    <Td>{i.firm_name || '—'}</Td>
                    <Td>{TYPE_LABELS[i.investor_type] || i.investor_type || '—'}</Td>
                    <Td><StatusPill status={i.status} /></Td>
                    <Td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                        {meta.count > 0 && (
                          <span title={`${meta.count} document${meta.count === 1 ? '' : 's'} on file`}
                            style={{ fontSize: 11, color: '#3a6ad9', fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                            📄 {meta.count}
                          </span>
                        )}
                        {meta.ndaStatus === 'pending' && (
                          <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 9999, background: '#fffde4', color: '#7a5200' }}>
                            NDA Pending
                          </span>
                        )}
                        {meta.ndaStatus === 'signed' && (
                          <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 9999, background: '#f0fde4', color: '#111111' }}>
                            NDA ✓
                          </span>
                        )}
                        {meta.count === 0 && !meta.ndaStatus && <span style={{ color: '#9ca3af', fontSize: 11 }}>—</span>}
                      </div>
                    </Td>
                    <Td>{fmtDate(i.created_at)}</Td>
                    <Td>{fmtDate(i.last_login_at)}</Td>
                    <Td align="right">{i.bid_count || 0}</Td>
                    <Td align="right" onClick={(e) => e.stopPropagation()}>
                      {i.status === 'pending' ? (
                        <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                          <button
                            disabled={busyId === i.id}
                            onClick={() => updateStatus(i.id, 'approved')}
                            style={btnGreen}
                          >
                            Approve
                          </button>
                          <button
                            disabled={busyId === i.id}
                            onClick={() => setRejecting(i)}
                            style={btnRed}
                          >
                            Reject
                          </button>
                        </div>
                      ) : i.status === 'approved' ? (
                        <button
                          disabled={busyId === i.id}
                          onClick={() => updateStatus(i.id, 'suspended')}
                          style={btnGray}
                        >
                          Suspend
                        </button>
                      ) : (
                        <span style={{ color: '#9ca3af', fontSize: 12 }}>—</span>
                      )}
                    </Td>
                    <Td align="right">
                      <span style={{ color: '#9ca3af', fontSize: 16 }}>›</span>
                    </Td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Side panel — slide in from right when an investor row is clicked.
            Refreshes the row's doc-count meta on any panel-driven mutation
            (upload / delete / send-NDA) via the onChange callback. */}
        <InvestorSidePanel
          investor={panelInvestor}
          onClose={() => setPanelInvestor(null)}
          onChange={() => hydrateDocMeta(investors)}
        />

        {rejecting && (
          <div style={modalBackdrop}>
            <div style={modalBox}>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#111827', marginBottom: 10 }}>
                Reject {rejecting.first_name} {rejecting.last_name}?
              </div>
              <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 14 }}>
                The investor will receive an email letting them know their
                application was not approved. Optional reason will be included.
              </p>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Optional reason (shared with the investor)"
                style={{
                  width: '100%',
                  minHeight: 80,
                  padding: 10,
                  borderRadius: 8,
                  border: '1px solid #e5e7eb',
                  fontSize: 13,
                  fontFamily: 'inherit',
                  resize: 'vertical',
                  marginBottom: 14,
                  boxSizing: 'border-box',
                }}
              />
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button
                  onClick={() => { setRejecting(null); setRejectReason(''); }}
                  style={btnGhost}
                >
                  Cancel
                </button>
                <button
                  onClick={() => updateStatus(rejecting.id, 'rejected', rejectReason)}
                  disabled={busyId === rejecting.id}
                  style={btnRed}
                >
                  {busyId === rejecting.id ? 'Rejecting…' : 'Confirm Reject'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </MarketLayout>
  );
}

const Th = ({ children, align = 'left' }) => (
  <th style={{
    textAlign: align,
    padding: '10px 16px',
    fontSize: 11,
    fontWeight: 700,
    color: '#6b7280',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  }}>{children}</th>
);
const Td = ({ children, align = 'left' }) => (
  <td style={{ padding: '12px 16px', textAlign: align, color: '#111827', verticalAlign: 'top' }}>{children}</td>
);

const btnBase = {
  fontSize: 12,
  fontWeight: 700,
  padding: '6px 12px',
  borderRadius: 8,
  border: 'none',
  cursor: 'pointer',
};
const btnGreen = { ...btnBase, backgroundColor: '#acf44e', color: '#fff' };
const btnRed   = { ...btnBase, backgroundColor: '#ff082f', color: '#fff' };
const btnGray  = { ...btnBase, backgroundColor: '#e5e7eb', color: '#374151' };
const btnGhost = { ...btnBase, backgroundColor: 'transparent', color: '#6b7280', border: '1px solid #e5e7eb' };

const modalBackdrop = {
  position: 'fixed',
  inset: 0,
  backgroundColor: 'rgba(15, 23, 42, 0.6)',
  zIndex: 80,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 16,
};
const modalBox = {
  backgroundColor: '#fff',
  borderRadius: 14,
  padding: 24,
  maxWidth: 460,
  width: '100%',
};
